import deepmerge from 'deepmerge';
import { useEffect, useMemo, useState } from 'react';
import { usePageContext } from '../contexts/page';
import { store } from '../redux';
import { useDynamicPaths } from './use-dynamic-paths';
export const usePage = (props = {}) => {
    const { jaenPage, jaenPages } = usePageContext();
    const paths = useDynamicPaths({
        staticPages: (jaenPages || [])
    });
    const id = useMemo(() => {
        if (props?.id) {
            return props.id;
        }
        else if (props?.path) {
            if (jaenPages == null) {
                throw new Error('Unable to resolve page by path. No pages provided.');
            }
            const path = props?.path;
            const newId = paths[path]?.jaenPageId;
            if (!newId) {
                throw new Error(`Could not resolve page by path: ${path}`);
            }
            return newId;
        }
        return jaenPage.id;
    }, [jaenPage, jaenPages, props, paths]);
    const [dynamicPage, setDynamicPage] = useState(undefined);
    useEffect(() => {
        const dynamicPage = store.getState().page.pages.nodes[id];
        setDynamicPage(dynamicPage);
        const unsubscribe = store.subscribe(() => {
            const state = store.getState();
            const dynamicPage = state.page.pages.nodes[id];
            const populateDynamicPage = (page) => {
                page.childPages = page.childPages?.map(childPage => {
                    const childPageData = state.page.pages.nodes[childPage.id];
                    let populatedPage = {
                        ...childPage,
                        ...childPageData
                    };
                    populatedPage = populateDynamicPage(populatedPage);
                    return populatedPage;
                });
                const mediaPage = store.getState().page.pages.nodes['JaenPage /cms/media/'];
                page.mediaNodes = (Object.values(mediaPage?.jaenFields['IMA:MEDIA_NODES']?.media_nodes?.value || {}).filter((mediaNode) => {
                    return page.id === mediaNode.jaenPageId;
                }) || []).map((mediaNode) => {
                    return {
                        id: mediaNode.id,
                        node: {
                            childImageSharp: {
                                gatsbyImageData: {
                                    placeholder: {
                                        fallback: mediaNode.preview?.url
                                    },
                                    layout: 'constrained',
                                    images: {
                                        fallback: {
                                            src: mediaNode.url
                                        }
                                    },
                                    width: mediaNode.width,
                                    height: mediaNode.height
                                }
                            }
                        },
                        description: mediaNode.description || ''
                    };
                });
                return page;
            };
            if (dynamicPage) {
                setDynamicPage(populateDynamicPage({
                    ...dynamicPage,
                    id
                }));
            }
        });
        return () => {
            unsubscribe();
        };
    }, [id]);
    const page = useMemo(() => {
        const staticPage = jaenPage.id === id ? jaenPage : jaenPages?.find(page => page.id === id);
        const mergedPage = deepmerge(staticPage || {}, dynamicPage || {});
        if (props.injectMedia) {
            let mediaNodes = mergedPage.mediaNodes;
            const replaceJaenFieldsImageValueIfImageField = (value) => {
                mediaNodes = value.mediaNodes || mediaNodes;
                for (const key in value.jaenFields) {
                    const field = value.jaenFields[key];
                    if (key === 'IMA:ImageField') {
                        for (const value of Object.values(field)) {
                            const mediaNode = mediaNodes.find(mediaNode => mediaNode.id === value.value);
                            if (mediaNode) {
                                value.value = mediaNode;
                            }
                        }
                    }
                }
            };
            replaceJaenFieldsImageValueIfImageField(mergedPage);
            for (const section of mergedPage.sections) {
                for (const item of section.items) {
                    replaceJaenFieldsImageValueIfImageField(item);
                }
            }
            for (const childPage of mergedPage.childPages) {
                replaceJaenFieldsImageValueIfImageField(childPage);
            }
        }
        return mergedPage;
    }, [id, dynamicPage]);
    return page;
};
